import { useFormikContext } from "formik";
import React from "react";
import Select from "../../../../components/form/select";
import MultiSelect from "../../../../components/form/select/multi";
import Switch from "../../../../components/form/switch";
import TextInput from "../../../../components/form/text-input";
import {
  CABLE_LENGTH_OPTIONS,
  CONNECTIVITY_OPTIONS,
  FILE_TYPE_OPTIONS,
  PACKAGE_OPTIONS,
} from "./constants";
import {
  firstSection,
  secondSection,
  thirdSection,
} from "../../constants/equipment-field-sections";
import baseStyles from "../base.module.scss";
import styles from "./styles.module.scss";

const getLabelValueOptions = (options) => {
  return Object.entries(options).map(([key, value]) => ({
    label: value,
    value: key,
  }));
};

export const EmsBootSection = ({ equipments }) => {
  const { handleChange, handleBlur, errors, touched, values, setFieldValue } =
    useFormikContext();

  return (
    <>
      <div
        className={`${styles.emsBootSection} ${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap2}`}
      >
        <div
          className={`${baseStyles.grid} ${baseStyles.gridCols3} ${baseStyles.gap2}`}
        >
          {firstSection.map((item) => (
            <div
              className={`${styles.emsBootCard}`}
              key={`${item.flagField}_card`}
            >
              <div className={`${styles.emsBootCardHeader}`}>
                <label className={`${styles.emsBootCardLabel}`}>
                  {item.title}
                </label>
                <Switch
                  id={`ems_boot_data.${item.flagField}`}
                  name={`ems_boot_data.${item.flagField}`}
                  checked={values.ems_boot_data[item.flagField]}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>
              <div className={styles.emsBootCardBody}>
                <TextInput
                  disabled={!values.ems_boot_data[item.flagField]}
                  label="Default Value"
                  id={`ems_boot_data_${item.valueField}`}
                  name={`ems_boot_data_${item.valueField}`}
                  value={values.ems_boot_data[item.valueField]}
                  prefix="$"
                  inputMode="decimal"
                  fullWidth
                  cleaveOptions={{
                    numeral: true,
                    numeralDecimalScale: 2,
                    numeralDecimalMark: ".",
                    numeralPositiveOnly: true,
                    onValueChanged: (e) => {
                      setFieldValue(
                        `ems_boot_data.${item.valueField}`,
                        e.target.rawValue,
                      );
                    },
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div
          style={{ width: "100%", height: "1px", backgroundColor: "#DEDEE0" }}
        />

        <div
          className={`${baseStyles.grid} ${baseStyles.gridCols3} ${baseStyles.gap2}`}
        >
          {secondSection
            .filter((item) => !item?.hideOnEquipmentForm)
            .map((item) => (
              <div
                className={`${styles.emsBootCard}`}
                key={`${item.flagField}_card`}
              >
                <div className={`${styles.emsBootCardHeader}`}>
                  <label className={`${styles.emsBootCardLabel}`}>
                    {item.title}
                  </label>

                  <Switch
                    id={`ems_boot_data.${item.flagField}`}
                    name={`ems_boot_data.${item.flagField}`}
                    checked={values.ems_boot_data[item.flagField]}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>

                {["hardware", "other_equipments"].indexOf(item.flagField) >
                  -1 &&
                  equipments?.length > 0 && (
                    <div className={`${styles.emsBootCardBody}`}>
                      <MultiSelect
                        search
                        disabled={!values.ems_boot_data[item.flagField]}
                        id={`ems_boot_data.${item.valueField}`}
                        name={`ems_boot_data.${item.valueField}`}
                        value={
                          values.ems_boot_data[item.valueField]
                            ? equipments
                                .filter((equipment) =>
                                  values.ems_boot_data[item.valueField]
                                    .split(",")
                                    .includes(equipment.id.toString()),
                                )
                                .map((equipment) => ({
                                  label: equipment.equipment_name,
                                  value: equipment.id.toString(),
                                }))
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const newValue = selectedOptions
                            .map((option) => option.value)
                            .join(",");
                          setFieldValue(
                            `ems_boot_data.${item.valueField}`,
                            newValue,
                          );
                        }}
                        options={equipments.map((equipment) => ({
                          label: equipment.equipment_name,
                          value: equipment.id.toString(),
                        }))}
                      />
                    </div>
                  )}

                {item.flagField === "add_on_equipment" &&
                  equipments?.length > 0 && (
                    <div className={`${styles.emsBootCardBody}`}>
                      <MultiSelect
                        search
                        disabled={!values.ems_boot_data[item.flagField]}
                        id={`ems_boot_data.${item.valueField}`}
                        name={`ems_boot_data.${item.valueField}`}
                        value={
                          values.ems_boot_data[item.valueField]
                            ? equipments
                                .filter((equipment) => {
                                  const currentValue =
                                    values.ems_boot_data[item.valueField];

                                  const selectedIds = currentValue
                                    .split(",")
                                    .filter((id) => id.trim() !== "");
                                  return selectedIds.includes(
                                    equipment.id.toString(),
                                  );
                                })
                                .map((equipment) => ({
                                  label: equipment.equipment_name,
                                  value: equipment.id.toString(),
                                }))
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const newValue = selectedOptions
                            .map((option) => option.value)
                            .join(",");
                          setFieldValue(
                            `ems_boot_data.${item.valueField}`,
                            newValue || "",
                          );
                        }}
                        options={equipments.map((equipment) => ({
                          label: equipment.equipment_name,
                          value: equipment.id.toString(),
                        }))}
                      />
                    </div>
                  )}

                {item.flagField === "file_types" && (
                  <div className={`${styles.emsBootCardBody}`}>
                    <MultiSelect
                      disabled={!values.ems_boot_data[item.flagField]}
                      id={`ems_boot_data.${item.valueField}`}
                      name={`ems_boot_data.${item.valueField}`}
                      value={
                        values.ems_boot_data[item.valueField]
                          ? getLabelValueOptions(FILE_TYPE_OPTIONS).filter(
                              (opt) =>
                                values.ems_boot_data[item.valueField]
                                  .split(",")
                                  .includes(opt.value),
                            )
                          : []
                      }
                      onChange={(selectedOptions) => {
                        const newValue = selectedOptions
                          .map((option) => option.value)
                          .join(",");
                        setFieldValue(
                          `ems_boot_data.${item.valueField}`,
                          newValue,
                        );
                      }}
                      options={getLabelValueOptions(FILE_TYPE_OPTIONS)}
                    />
                  </div>
                )}

                {item.flagField === "package" && (
                  <div className={`${styles.emsBootCardBody}`}>
                    <MultiSelect
                      disabled={!values.ems_boot_data[item.flagField]}
                      id={`ems_boot_data.${item.valueField}`}
                      name={`ems_boot_data.${item.valueField}`}
                      value={
                        values.ems_boot_data[item.valueField]
                          ? getLabelValueOptions(PACKAGE_OPTIONS).filter(
                              (opt) =>
                                values.ems_boot_data[item.valueField]
                                  .split(",")
                                  .includes(opt.value),
                            )
                          : []
                      }
                      onChange={(selectedOptions) => {
                        const newValue = selectedOptions
                          .map((option) => option.value)
                          .join(",");
                        setFieldValue(
                          `ems_boot_data.${item.valueField}`,
                          newValue,
                        );
                      }}
                      options={getLabelValueOptions(PACKAGE_OPTIONS)}
                    />
                  </div>
                )}

                {item.flagField === "cable_length" && (
                  <div className={`${styles.emsBootCardBody}`}>
                    <MultiSelect
                      disabled={!values.ems_boot_data[item.flagField]}
                      id={`ems_boot_data.${item.valueField}`}
                      name={`ems_boot_data.${item.valueField}`}
                      value={
                        values.ems_boot_data[item.valueField]
                          ? getLabelValueOptions(CABLE_LENGTH_OPTIONS).filter(
                              (opt) =>
                                values.ems_boot_data[item.valueField]
                                  .split(",")
                                  .includes(opt.value),
                            )
                          : []
                      }
                      onChange={(selectedOptions) => {
                        const newValue = selectedOptions
                          .map((option) => option.value)
                          .join(",");
                        setFieldValue(
                          `ems_boot_data.${item.valueField}`,
                          newValue,
                        );
                      }}
                      options={getLabelValueOptions(CABLE_LENGTH_OPTIONS)}
                    />
                  </div>
                )}

                {item.flagField === "connectivity" && (
                  <div className={`${styles.emsBootCardBody}`}>
                    <div
                      className={`${baseStyles.grid} ${baseStyles.gridCols1} ${baseStyles.gap1}`}
                    >
                      <MultiSelect
                        disabled={!values.ems_boot_data[item.flagField]}
                        id={`ems_boot_data.connectivity_options`}
                        name={`ems_boot_data.connectivity_options`}
                        value={
                          values.ems_boot_data.connectivity_options
                            ? getLabelValueOptions(CONNECTIVITY_OPTIONS).filter(
                                (opt) =>
                                  values.ems_boot_data.connectivity_options
                                    .split(",")
                                    .includes(opt.value),
                              )
                            : []
                        }
                        onChange={(selectedOptions) => {
                          const newValue = selectedOptions
                            .map((option) => option.value)
                            .join(",");
                          setFieldValue(
                            `ems_boot_data.connectivity_options`,
                            newValue,
                          );
                        }}
                        options={getLabelValueOptions(CONNECTIVITY_OPTIONS)}
                      />

                      <Select
                        disabled={!values.ems_boot_data[item.flagField]}
                        id={`ems_boot_data.connectivity_default`}
                        name={`ems_boot_data.connectivity_default`}
                        placeholderAsBlankOption
                        options={getLabelValueOptions(CONNECTIVITY_OPTIONS)}
                        value={values.ems_boot_data.connectivity_default}
                        onChange={(value) => {
                          setFieldValue(
                            `ems_boot_data.connectivity_default`,
                            value,
                          );
                        }}
                        placeholder="Select default connectivity"
                      />
                    </div>
                  </div>
                )}

                {item.type === "string" && (
                  <div className={`${styles.emsBootCardBody}`}>
                    <TextInput
                      label="Default Value"
                      disabled={!values.ems_boot_data[item.flagField]}
                      id={`ems_boot_data.${item.valueField}`}
                      name={`ems_boot_data.${item.valueField}`}
                      value={values.ems_boot_data[item.valueField]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </div>
                )}
              </div>
            ))}
        </div>

        <div
          style={{ width: "100%", height: "1px", backgroundColor: "#DEDEE0" }}
        />

        <div
          className={`${baseStyles.grid} ${baseStyles.gridCols3} ${baseStyles.gap2}`}
        >
          {thirdSection.map((item) => (
            <div
              className={`${styles.emsBootCard} ${styles.emsBootCardBoolean}`}
              key={`${item.flagField}_card`}
            >
              <div className={`${styles.emsBootCardHeader}`}>
                <label className={`${styles.emsBootCardLabel}`}>
                  {item.title}
                </label>
                <Switch
                  id={`ems_boot_data.${item.flagField}`}
                  name={`ems_boot_data.${item.flagField}`}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  checked={values.ems_boot_data[item.flagField]}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
